/* eslint-disable unicorn/no-await-expression-member */
/* eslint-disable import/namespace */
'use client'

import { Checkbox, MenuList, FormControlLabel, ListItem } from '@mui/material'
import {
  type CategoryLevelProps,
  type CategoryRowProps,
} from './filter-header-types'
import { type Category } from '@/lib/generated/graphql'
import { Suspense, useState } from 'react'
import { useAsync } from 'react-use'
import { hostURL } from '@/utils/functions/url-utils'

import { type LegacyRef, useMemo, useRef } from 'react'

import { Icon } from '@/icons/icon'
import ListItemButton from '@mui/material/ListItemButton'
import { Transition } from 'react-transition-group'
import { useRouter } from 'next/navigation'
import { useGetRedirectPath } from '@/components/utility/product-filters/product-filters'
import xor from 'lodash/xor'
import times from 'lodash/times'
import * as Sentry from '@sentry/nextjs'

const sortDataByCategory1 = (categories: Category[]) =>
  categories.sort((a, b) => {
    let aLevel = a.category_level1
    let bLevel = b.category_level1
    if (a.category_categorylevel === 2) {
      aLevel = a.category_level2
      bLevel = b.category_level2
    } else if (a.category_categorylevel === 3) {
      aLevel = a.category_level3
      bLevel = b.category_level3
    }

    if (aLevel != null && bLevel != null) {
      if (aLevel < bLevel) {
        return -1
      }
      if (aLevel > bLevel) {
        return 1
      }
    }
    return 0
  })

const useGetCats = (enabled: boolean, categoryId: string) => {
  return useAsync(async () => {
    try {
      if (!enabled) return null
      const resp = await fetch(`${hostURL()}/api/category`, {
        body: JSON.stringify({
          parentCategory: categoryId,
        }),
        method: 'POST',
      })
      return (await resp.json()) as {
        getCategories: { items: Category[]; __typename: 'categoryList' }
      }
    } catch (error) {
      Sentry.captureException(error)
    }
  })
}

const CategorySkeleton = () => {
  const elements: JSX.Element[] = []
  times(5, (index) => {
    elements.push(
      <div
        className='flex h-8 w-[180px] flex-row items-center p-1 pl-4'
        key={index}
      >
        <Checkbox />
        <div className='h-6 w-[70px] animate-pulse bg-gray-200' />
      </div>,
    )
  })
  return <div>{elements}</div>
}

const CategoryRow = ({
  categoryId,
  categoryLevel,
  selected,
  categoryName,
  productFilters,
  enabled,
}: CategoryRowProps) => {
  const [open, setOpen] = useState(false)
  const { value: data } = useGetCats(enabled, categoryId)
  const { getRedirectPath } = useGetRedirectPath()
  const router = useRouter()

  const ref = useRef<HTMLElement>()
  const handleOpen = () => {
    setOpen(!open)
  }

  const isIndeterminate = useMemo(
    () =>
      selected.some((sel) => {
        if (categoryLevel === 1) {
          return (
            sel.category_level2 === categoryName ||
            sel.category_level1 === categoryName
          )
        }
        return sel.category_level2 === categoryName
      }),
    [categoryName, selected, categoryLevel],
  )

  const selectedcats = selected.map((cat) => cat.category_categoryId)

  const handleClick = () => {
    const updatedCategoriesFilter = xor(selectedcats, [categoryId]) as string[]
    const redirectPath = getRedirectPath(updatedCategoriesFilter, 'categories')
    router.push(redirectPath, {
      scroll: false,
    })
  }

  const isChecked = selected.some(
    (select) => select.category_categoryId === categoryId,
  )

  const childrenUnfiltered =
    data?.getCategories?.__typename === 'categoryList'
      ? data.getCategories.items
      : undefined

  const categories =
    childrenUnfiltered == null
      ? undefined
      : childrenUnfiltered.filter(
          (item) => item?.category_categoryId !== categoryId,
        )
  const transitionStyles = {
    entering: {
      height:
        (categories?.length ?? 0) > 0 ? (categories?.length ?? 1) * 40 - 4 : 0,
    },
    entered: { height: 'auto' },
    exiting: { height: ref.current?.offsetHeight ?? 0 },
    exited: { height: 0 },
    unmounted: { height: 0 },
  }

  return (
    <ListItem className='flex flex-col justify-between overflow-hidden bg-white p-1 pl-4 transition-all '>
      <div className='z-[2] -m-1 -ml-4 flex h-10 w-full bg-white'>
        <FormControlLabel
          sx={{
            flex: 1,
            '& .MuiCheckbox-root': {
              color: '#713FFF !important',
            },
          }}
          control={
            <Checkbox
              checked={isChecked}
              onClick={handleClick}
              indeterminate={!isChecked && isIndeterminate}
            />
          }
          label={categoryName}
        />
        {(categories?.length ?? 0) === 0 ? (
          <div />
        ) : (
          <div>
            <ListItemButton onClick={handleOpen}>
              <div
                className={`${open ? 'rotate-0' : 'rotate-180'} transition-all`}
              >
                <Icon color='dark' size='xs' name='arrow-up-1-regular' />
              </div>
            </ListItemButton>
          </div>
        )}
      </div>
      {(categories?.length ?? 0) > 0 && (
        <Transition in={open} timeout={150} nodeRef={ref}>
          {(state) => (
            <div
              ref={ref as LegacyRef<HTMLDivElement>}
              style={{
                transition: 'height 150ms',
                width: '100%',
                overflow: 'hidden',
                ...transitionStyles[state],
              }}
            >
              <CategoryLevel
                open={open}
                categories={categories as Category[]}
                productFilters={productFilters}
                selected={selected}
                categoryId={categoryId}
                categoryLevel={categoryLevel}
              />
            </div>
          )}
        </Transition>
      )}
    </ListItem>
  )
}

export const CategoryLevel = ({
  categoryId,
  categories = [],
  selected,
  open,
  onFilterSelect,
  productFilters,
}: CategoryLevelProps) => {
  const data = categories.filter(
    (cat) => cat.category_categoryId !== categoryId,
  )
  const sortData = sortDataByCategory1(data)

  return (
    <MenuList className='bottom-0 w-full min-w-fit bg-white py-0'>
      {sortData?.map((item) => {
        const categoryName = (
          (item.category_friendlyName?.length ?? 0) > 0
            ? item.category_friendlyName
            : (item.category_name?.length ?? 0) > 0
              ? item.category_name
              : ''
        ) as string
        return (
          <Suspense fallback={<CategorySkeleton />}>
            <CategoryRow
              categories={categories}
              key={item.category_categoryId}
              categoryId={item.category_categoryId ?? ''}
              categoryLevel={item.category_categorylevel ?? 0}
              categoryName={categoryName}
              selected={selected}
              enabled={open}
              onFilterSelect={onFilterSelect}
              productFilters={productFilters}
            ></CategoryRow>
          </Suspense>
        )
      })}
    </MenuList>
  )
}
